import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Shield, AlertTriangle, CheckCircle2, FileText, PenLine } from 'lucide-react';

const TripConfirmationModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  booking, 
  userRole,
  loading 
}) => {
  const [checkboxes, setCheckboxes] = useState({
    vehicle_condition: false,
    rules_understood: false,
    emergency_contact: false,
    insurance_coverage: false,
    return_commitment: false
  });
  const [signatureName, setSignatureName] = useState('');

  const allChecked = Object.values(checkboxes).every(v => v);
  const canSubmit = allChecked && signatureName.trim().length >= 2;

  const handleCheckboxChange = (key) => {
    setCheckboxes(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSubmit = () => {
    if (!canSubmit) return;

    const deviceInfo = {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    };

    onConfirm({
      checkboxes_confirmed: true,
      checkboxes,
      signature_name: signatureName.trim(),
      device_info: JSON.stringify(deviceInfo)
    });
  };

  const checklistItems = [
    {
      key: 'vehicle_condition',
      label: 'I confirm that I have inspected the vehicle and verified its condition matches the pre-hire checklist'
    },
    {
      key: 'rules_understood',
      label: 'I understand and agree to the host\'s rules including kilometre limits, fuel policy, and restrictions'
    },
    {
      key: 'emergency_contact',
      label: 'I have saved the emergency contact information and know how to report breakdowns'
    },
    {
      key: 'insurance_coverage',
      label: 'I acknowledge the insurance coverage details and understand my liability obligations'
    },
    {
      key: 'return_commitment',
      label: `I commit to returning the vehicle by ${booking?.end_date} at the agreed location`
    }
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="mx-4 max-w-[calc(100vw-2rem)] sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Shield className="w-5 h-5 text-[#004B8D]" />
            Trip Start Confirmation
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Important Notice */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-medium mb-1">Important Legal Notice</p>
                <p className="text-xs">
                  By confirming below, you are digitally signing this agreement. 
                  Your signature, IP address, and timestamp will be recorded as a legally binding confirmation.
                  {userRole === 'owner' 
                    ? ' You confirm the vehicle is ready for handover.'
                    : ' You confirm receipt of the vehicle in the documented condition.'}
                </p>
              </div>
            </div>
          </div>

          {/* Confirmation Checkboxes */}
          <div className="space-y-3">
            <Label className="text-sm font-medium flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Confirmation Checklist
            </Label>
            {checklistItems.map((item) => (
              <div 
                key={item.key} 
                className="flex items-start gap-3 p-2 rounded-md hover:bg-slate-50 transition-colors"
              >
                <Checkbox
                  id={item.key}
                  checked={checkboxes[item.key]}
                  onCheckedChange={() => handleCheckboxChange(item.key)}
                  className="mt-0.5"
                  data-testid={`checkbox-${item.key}`}
                />
                <label 
                  htmlFor={item.key}
                  className="text-sm text-slate-700 cursor-pointer leading-snug"
                >
                  {item.label}
                </label>
              </div>
            ))}
          </div>

          {/* Digital Signature */}
          <div className="pt-2 border-t">
            <Label className="text-sm font-medium flex items-center gap-2 mb-2">
              <PenLine className="w-4 h-4" />
              Digital Signature
            </Label>
            <p className="text-xs text-slate-500 mb-2">
              Type your full legal name exactly as it appears on your ID to sign this confirmation.
            </p>
            <Input
              type="text"
              value={signatureName}
              onChange={(e) => setSignatureName(e.target.value)}
              placeholder="Enter your full legal name"
              className="font-medium"
              data-testid="signature-input"
            />
            {signatureName.trim().length > 0 && signatureName.trim().length < 2 && (
              <p className="text-xs text-red-500 mt-1">Name must be at least 2 characters</p>
            )}
          </div>

          {/* Summary */}
          <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-600 space-y-1">
            <p><strong>Booking:</strong> {booking?.vehicle?.make} {booking?.vehicle?.model}</p>
            <p><strong>Period:</strong> {booking?.start_date} to {booking?.end_date}</p>
            <p><strong>Your Role:</strong> {userRole === 'owner' ? 'Host' : 'Renter'}</p>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2 pt-2">
          <Button 
            variant="outline" 
            onClick={onClose} 
            className="w-full sm:w-auto"
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit || loading}
            className="w-full sm:w-auto bg-[#004B8D] hover:bg-[#003865]"
            data-testid="confirm-trip-start-btn"
          >
            {loading ? (
              'Submitting...'
            ) : (
              <>
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Confirm & Sign
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TripConfirmationModal;
